import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAppStore } from "../../store/useAppStore";
import { Button } from "../../components/ui/Button";
import { Chip } from "../../components/ui/Chip";

export function InspectionReview({ inspectionId }: { inspectionId: string }) {
  const navigate = useNavigate();
  const inspection = useAppStore((s) => s.inspections.find((i) => i.id === inspectionId));
  const actions = useAppStore((s) => s.generatedActions.filter((a) => inspection?.items.some((i) => i.generatedActionId === a.id)));
  const reviewGeneratedAction = useAppStore((s) => s.reviewGeneratedAction);
  const finishInspectionReview = useAppStore((s) => s.finishInspectionReview);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  if (!inspection) return null;

  const pending = actions.filter((a) => a.status === "draft");
  const decided = actions.filter((a) => a.status !== "draft");

  if (inspection.status === "completed") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="font-display text-[22px] font-black text-brand-navy">Inspection complete</p>
        <p className="max-w-xs text-[13.5px] text-muted-foreground">
          {decided.filter((a) => a.status !== "discarded").length} action{decided.filter((a) => a.status !== "discarded").length === 1 ? "" : "s"} accepted into Work.
        </p>
        <Button onClick={() => navigate("/work")}>Go to Work</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div>
        <h1 className="font-display text-[22px] font-black tracking-tight text-brand-navy">Review generated actions</h1>
        <p className="text-[13px] text-muted-foreground">
          Attention/Fail results from this walk drafted the actions below. Nothing is created in Work until you decide.
        </p>
      </div>

      {actions.length === 0 && <p className="rounded-md border border-dashed border-border p-6 text-center text-[13.5px] text-muted-foreground">No attention or fail results this walk — nothing to review.</p>}

      <ul className="flex flex-col gap-3">
        {actions.map((action) => (
          <li key={action.id} className="rounded-md border border-border bg-card p-3.5">
            <div className="flex items-start justify-between gap-2">
              {editingId === action.id ? (
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={2}
                  className="w-full rounded-sm border border-border p-2 text-[13.5px]"
                />
              ) : (
                <div>
                  <p className="font-action text-[14.5px] font-bold text-foreground">{action.title}</p>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">{action.description}</p>
                </div>
              )}
              <Chip tone={action.severity === "fail" ? "danger" : "attention"}>{action.severity}</Chip>
            </div>
            <div className="mt-2.5 flex gap-2">
              {action.status === "draft" ? (
                editingId === action.id ? (
                  <>
                    <Button
                      onClick={() => {
                        reviewGeneratedAction(action.id, "edited", { title: action.title, description: editText });
                        setEditingId(null);
                      }}
                    >
                      Save + accept
                    </Button>
                    <Button variant="ghost" onClick={() => setEditingId(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => reviewGeneratedAction(action.id, "accepted")}>Accept</Button>
                    <Button
                      variant="quiet"
                      onClick={() => {
                        setEditingId(action.id);
                        setEditText(action.description);
                      }}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" onClick={() => reviewGeneratedAction(action.id, "discarded")}>
                      Discard
                    </Button>
                  </>
                )
              ) : (
                <p className="font-mono text-[11px] uppercase text-muted-foreground">{action.status}</p>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Button className="mt-auto" disabled={pending.length > 0} onClick={() => finishInspectionReview(inspection.id)}>
        {pending.length > 0 ? `Decide on ${pending.length} remaining action${pending.length === 1 ? "" : "s"}` : "Finish inspection"}
      </Button>
    </div>
  );
}
